import { fatal, readCookie } from '../utils';

/**
 * Three roots, three sources — and they are genuinely different (§5.2).
 *
 *   PACS    data-source config, written once from cookie `pacsUrl` (see
 *           applyPacsRootFromCookie in index.tsx). Read via getConfig(), which
 *           is the only source correct in BOTH environments: locally there is
 *           no pacsUrl cookie at all, and sycai-local.js sets /wado and
 *           /pacs/dicom-web statically (design D4).
 *   viewer  cookie `viewerUrl`. The config key
 *           `implementation.studyManagement.server.baseUrl` is DROPPED (§5.2) —
 *           one build serves multiple environments, chosen at runtime.
 *   auth    appConfig, NOT a cookie:
 *           implementation.environmentManagement.server.baseUrl.
 *
 * Depends on ExtensionManager.ts:283 (appConfig passed to preRegistration) and
 * :673 (the `appConfig` getter returning the same object) being the SAME
 * reference, and on :677's `activeDataSourceName` / :387's `getActiveDataSource`.
 * If upstream ever clones appConfig between those two points, a value read after
 * boot stops being the live one and getAuthRoot/getLoginUrl silently serve stale
 * configuration.
 */
let managers: { extensionManager: AppTypes.ExtensionManager } | null = null;

/**
 * Called once, first thing in preRegistration. Deliberately accepts a falsy
 * argument without complaint: M1's registration.test.ts:73 drives preRegistration
 * with no extensionManager, and boot ordering is not a fault until something
 * actually reads a root. requireExtensionManager is where that is caught.
 */
export function initRoots(extensionManager: AppTypes.ExtensionManager): void {
  managers = { extensionManager };
}

function requireExtensionManager(): AppTypes.ExtensionManager {
  // `!managers?.extensionManager`, not `!managers`: initRoots(undefined) assigns a
  // truthy wrapper around an undefined manager, so testing the wrapper alone would
  // return undefined here and hand the caller a bare
  // `TypeError: Cannot read properties of undefined (reading 'appConfig')` —
  // exactly the illegible failure this named error exists to replace.
  if (!managers?.extensionManager) {
    fatal('config/roots used before initRoots() ran in preRegistration');
  }
  return managers.extensionManager;
}

function requireAppConfigPath(path: string): string {
  const appConfig = requireExtensionManager().appConfig as Record<string, any>;
  const value = path.split('.').reduce((node, key) => node?.[key], appConfig);

  if (typeof value !== 'string' || !value) {
    fatal(
      `appConfig.${path} is not set. The active app config must carry an ` +
        `implementation.environmentManagement block — see sycai-local.js.`
    );
  }

  // Returned verbatim. Normalising here would reach getLoginUrl too, and a login
  // URL may legitimately end in '/' inside a query or fragment
  // ('…/login?redirect=/'), where stripping it loses a meaningful character.
  // Only getAuthRoot, a base API root, normalises.
  return value;
}

/** Environment-management API root. Consumed by authHttp. */
export function getAuthRoot(): string {
  return requireAppConfigPath('implementation.environmentManagement.server.baseUrl').replace(
    /\/+$/,
    ''
  );
}

/** Where handleUnauthenticated sends the browser. Not normalised — see above. */
export function getLoginUrl(): string {
  return requireAppConfigPath('implementation.environmentManagement.client.loginUrl');
}

/**
 * Study-management root. First consumer is M3's viewerHttp; it lives here so
 * §5.2's three-root story is in one file rather than discovered twice.
 */
export function getViewerRoot(): string {
  const viewerUrl = readCookie('viewerUrl');

  // .trim() BEFORE the guard, matching applyPacsRootFromCookie (index.tsx:41). A
  // whitespace-only cookie passes a bare truthiness test and becomes a base URL of
  // "   /diagnoses/status".
  const root = viewerUrl?.trim().replace(/\/+$/, '');

  if (!root) {
    fatal('the viewerUrl cookie is absent or empty');
  }

  return root;
}

/**
 * The read shared by `getPacsRoot` and `tryGetPacsRoot`: pull `wadoRoot` off the active
 * data source and strip the `/dicom-web` suffix. Returns `undefined` on either failure —
 * no `extensionManager`, or a data source that reports no `wadoRoot` — rather than
 * choosing how loud to be about it; that choice belongs to the two callers below, not to
 * this function.
 */
function resolvePacsRoot(): string | undefined {
  if (!managers?.extensionManager) {
    return undefined;
  }

  const dataSource = managers.extensionManager.getActiveDataSource()?.[0];
  const wadoRoot = dataSource?.getConfig?.()?.wadoRoot;

  if (!wadoRoot) {
    return undefined;
  }

  return String(wadoRoot).replace(/\/dicom-web\/?$/, '');
}

/**
 * PACS root. Read from the DATA SOURCE, never from the cookie (D4) — the cookie
 * only writes the roots in, once, at boot. Locally wadoRoot is '/pacs/dicom-web'
 * and this returns '/pacs'.
 *
 * Callable only AFTER route entry. DicomWebDataSource.getConfig() returns
 * `dicomWebConfigCopy` (extensions/default/src/DicomWebDataSource/index.ts:779-781),
 * which is assigned inside initialize() (:208) — so during preRegistration it is
 * still undefined and this throws the no-wadoRoot error. If a caller ever needs it
 * earlier, read `extensionManager.getDataSourceDefinition(name).configuration.wadoRoot`
 * instead, which is what applyPacsRootFromCookie writes.
 *
 * No caller left in the tree after M4.2's whole-branch review (Important 2): orthancHttp,
 * the sole consumer, switched to `tryGetPacsRoot` below because it is a passive readout,
 * not the boot path. Kept anyway as the `fatal()`-on-failure sibling of `getAuthRoot`/
 * `getViewerRoot` above, per §5.4, for whichever future boot-path caller needs the PACS
 * root under that same loud-failure contract. A passive-UI caller wants `tryGetPacsRoot`
 * instead — see its own docblock for why loud failure is wrong there.
 */
export function getPacsRoot(): string {
  const extensionManager = requireExtensionManager();
  const root = resolvePacsRoot();

  if (!root) {
    fatal(`active data source "${extensionManager.activeDataSourceName}" reports no wadoRoot`);
  }

  return root;
}

/**
 * Non-fatal twin of `getPacsRoot`, for orthancHttp's `getBaseUrl` ONLY (M4.2 whole-branch
 * review, Important 2). `getPacsRoot`'s contract is boot-path, so `fatal()` there is
 * correct: §5.4 wants upstream's silent failures loud. But `orthancHttp`
 * is read from `QACheckItem`, a passive per-series readout mounted on every CT viewport —
 * the FIRST product-UI consumer of a PACS-root getter, not the boot path. `sycai-local.js`
 * declares `dicomjson`/`dicomlocal` data sources alongside `orthancProxy`, and neither
 * `DicomJSONDataSource` nor `DicomLocalDataSource` reports a `wadoRoot`; dropping a CT into
 * `/local` would otherwise call `fatal()` — which calls `globalThis.reportError` (utils/
 * fatal.ts) BEFORE throwing, escaping `HttpClient`'s own try/catch entirely — on every
 * mount, uncaught, with nothing cached to stop the retry. D-3 already governs this
 * transport as the one that must not escalate (no `onUnauthorized`, http/index.ts); an
 * uncaught global error from a passive readout is the same class of escalation `fatal()`
 * exists to produce deliberately elsewhere, misapplied here. Returning `undefined` instead
 * routes through `HttpClient.buildUrl`'s existing empty-baseUrl guard, a plain `Error`
 * caught by `fetchLabels` like any other transport failure — `describeError`'s docblock
 * already names that guard as one of the two sources of its `status 0` bucket.
 */
export function tryGetPacsRoot(): string | undefined {
  return resolvePacsRoot();
}
